/**
 * /control/bcp · Business Continuity Plan — Fase 18.5
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Zap, Plus, FileDown, Play, CheckCircle2, RefreshCw } from "lucide-react";
import { BCP_ESTADOS, fmtFecha } from "./_common";

export default function BCP() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [openCerrar, setOpenCerrar] = useState(null);
    const [form, setForm] = useState({
        tipo: "restauracion_semestral",
        fecha_planeada: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
        equipo_responsable_csv: "admin_ti@trafalgar.mx",
        alcance_csv: "api,mongo,pdf,object_storage,email",
        rto_objetivo_minutos: 60, rpo_objetivo_minutos: 15,
        notas: "",
    });
    const [cerrarForm, setCerrarForm] = useState({
        firma_responsable_email: "admin_ti@trafalgar.mx",
        firma_dg_email: "director@trafalgar.mx",
        conclusion: "",
    });

    const load = async () => {
        const r = await api.get("/control/bcp");
        setItems(r.data || []);
    };
    useEffect(() => { load(); }, []);

    const planear = async () => {
        try {
            await api.post("/control/bcp/planear", {
                tipo: form.tipo,
                fecha_planeada: new Date(form.fecha_planeada).toISOString(),
                equipo_responsable: form.equipo_responsable_csv.split(",").map(s => s.trim()).filter(Boolean),
                alcance: form.alcance_csv.split(",").map(s => s.trim()).filter(Boolean),
                rto_objetivo_minutos: Number(form.rto_objetivo_minutos),
                rpo_objetivo_minutos: Number(form.rpo_objetivo_minutos),
                notas: form.notas,
            });
            toast.success("BCP planeada");
            setOpen(false); load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        }
    };

    const ejecutar = async (bcp) => {
        // Simular ejecución con todos los servicios ok
        const resultados = (bcp.alcance || []).map(s => ({
            servicio: s, estado: "ok",
            tiempo_recuperacion_minutos: Math.floor(Math.random() * 30 + 5),
            notas: "Simulación OK",
        }));
        try {
            await api.post(`/control/bcp/${bcp.id}/ejecutar`, {
                resultados, evidencias_doc_ids: [],
            });
            toast.success("Ejecución registrada");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        }
    };

    const cerrar = async () => {
        if (!cerrarForm.conclusion || cerrarForm.conclusion.length < 10) {
            toast.error("Conclusión mínima 10 caracteres"); return;
        }
        try {
            await api.post(`/control/bcp/${openCerrar.id}/cerrar`, cerrarForm);
            toast.success("BCP cerrada · Acta generada");
            setOpenCerrar(null); load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        }
    };

    const descargar = (id) =>
        window.open(`${api.defaults.baseURL}/control/bcp/${id}/pdf`, "_blank");

    return (
        <div className="space-y-6" data-testid="bcp-list">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                        BCP · Business Continuity Plan
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        M-16 §4 · Pruebas semestrales de restauración · RTO/RPO documentados · Firma DG requerida para cerrar.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
                                data-testid="bcp-nuevo">
                                <Plus className="h-4 w-4 mr-1" /> Planear prueba
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Nueva prueba BCP</DialogTitle>
                                <DialogDescription>Programar prueba de restauración.</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-3">
                                <div>
                                    <label className="text-xs font-semibold">Tipo</label>
                                    <Select value={form.tipo} onValueChange={v => setForm({ ...form, tipo: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="restauracion_semestral">Restauración semestral</SelectItem>
                                            <SelectItem value="failover">Failover</SelectItem>
                                            <SelectItem value="prueba_crecimiento">Prueba de crecimiento</SelectItem>
                                            <SelectItem value="otra">Otra</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Fecha planeada</label>
                                    <Input type="date" value={form.fecha_planeada}
                                        onChange={e => setForm({ ...form, fecha_planeada: e.target.value })} />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Equipo responsable (emails coma)</label>
                                    <Input value={form.equipo_responsable_csv}
                                        onChange={e => setForm({ ...form, equipo_responsable_csv: e.target.value })} />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Alcance (servicios coma)</label>
                                    <Input value={form.alcance_csv}
                                        onChange={e => setForm({ ...form, alcance_csv: e.target.value })} />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs font-semibold">RTO (min)</label>
                                        <Input type="number" value={form.rto_objetivo_minutos}
                                            onChange={e => setForm({ ...form, rto_objetivo_minutos: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold">RPO (min)</label>
                                        <Input type="number" value={form.rpo_objetivo_minutos}
                                            onChange={e => setForm({ ...form, rpo_objetivo_minutos: e.target.value })} />
                                    </div>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                                <Button onClick={planear} data-testid="bcp-planear-btn">Planear</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <Card>
                <CardContent className="p-0">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">Folio</th>
                                <th className="px-3 py-2 text-left">Tipo</th>
                                <th className="px-3 py-2 text-left">Planeada</th>
                                <th className="px-3 py-2 text-left">Ejecutada</th>
                                <th className="px-3 py-2 text-right">RTO/RPO</th>
                                <th className="px-3 py-2 text-left">Cumple</th>
                                <th className="px-3 py-2 text-left">Estado</th>
                                <th className="px-3 py-2 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">
                                    Sin pruebas BCP registradas.
                                </td></tr>
                            )}
                            {items.map(b => {
                                const cfg = BCP_ESTADOS[b.estado] || { label: b.estado, cls: "" };
                                return (
                                    <tr key={b.id} className="border-t">
                                        <td className="px-3 py-2 font-mono text-xs">{b.folio_prueba}</td>
                                        <td className="px-3 py-2 text-xs">{b.tipo}</td>
                                        <td className="px-3 py-2 text-xs">{fmtFecha(b.fecha_planeada)}</td>
                                        <td className="px-3 py-2 text-xs">{fmtFecha(b.fecha_ejecutada)}</td>
                                        <td className="px-3 py-2 text-right text-xs">
                                            {b.rto_objetivo_minutos}/{b.rpo_objetivo_minutos} min
                                        </td>
                                        <td className="px-3 py-2 text-xs">
                                            {b.estado !== "planeada" && (
                                                <>
                                                    <Badge className={b.cumple_rto ? "bg-emerald-100 text-emerald-900 border-emerald-300 border" : "bg-red-100 text-red-900 border-red-300 border"}>
                                                        RTO
                                                    </Badge>{" "}
                                                    <Badge className={b.cumple_rpo ? "bg-emerald-100 text-emerald-900 border-emerald-300 border" : "bg-red-100 text-red-900 border-red-300 border"}>
                                                        RPO
                                                    </Badge>
                                                </>
                                            )}
                                        </td>
                                        <td className="px-3 py-2"><Badge className={cfg.cls}>{cfg.label}</Badge></td>
                                        <td className="px-3 py-2 text-right space-x-1">
                                            {b.estado === "planeada" && (
                                                <Button size="sm" variant="outline" onClick={() => ejecutar(b)}
                                                    data-testid={`bcp-ejecutar-${b.id}`}>
                                                    <Play className="h-3 w-3" />
                                                </Button>
                                            )}
                                            {["en_ejecucion", "observada"].includes(b.estado) && (
                                                <Button size="sm" variant="outline"
                                                    onClick={() => setOpenCerrar(b)}
                                                    data-testid={`bcp-cerrar-${b.id}`}>
                                                    <CheckCircle2 className="h-3 w-3" />
                                                </Button>
                                            )}
                                            {b.estado === "cerrada" && (
                                                <Button size="sm" variant="outline"
                                                    onClick={() => descargar(b.id)}>
                                                    <FileDown className="h-3 w-3" />
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </CardContent>
            </Card>

            <Dialog open={!!openCerrar} onOpenChange={v => !v && setOpenCerrar(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cerrar prueba BCP · {openCerrar?.folio_prueba}</DialogTitle>
                        <DialogDescription>
                            Requiere firma de Dirección General (distinta al responsable técnico).
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-semibold">Email responsable técnico</label>
                            <Input value={cerrarForm.firma_responsable_email}
                                onChange={e => setCerrarForm({ ...cerrarForm, firma_responsable_email: e.target.value })} />
                        </div>
                        <div>
                            <label className="text-xs font-semibold">Email Dirección General</label>
                            <Input value={cerrarForm.firma_dg_email}
                                onChange={e => setCerrarForm({ ...cerrarForm, firma_dg_email: e.target.value })} />
                        </div>
                        <div>
                            <label className="text-xs font-semibold">Conclusión (≥10 caracteres)</label>
                            <Textarea value={cerrarForm.conclusion}
                                onChange={e => setCerrarForm({ ...cerrarForm, conclusion: e.target.value })}
                                rows={3} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpenCerrar(null)}>Cancelar</Button>
                        <Button onClick={cerrar} data-testid="bcp-cerrar-firma-btn">Firmar y cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
